import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Package,
  ClipboardList,
  FileCheck,
  CheckSquare,
  CalendarDays,
  Wrench,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  LineChart,
  Shield,
  FileText,
  ScrollText,
  Truck,
  Wallet,
  Leaf,
  Map,
  Repeat,
  ShieldCheck,
  Bookmark,
  MessageSquare,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import OfflineStatusIndicator from '../offline/OfflineStatusIndicator';

const Sidebar = () => {
  const { user, logout, canManageUsers, isAdmin } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/sites', label: 'Sites', icon: Building2 },
    { path: '/assets', label: 'Assets', icon: Package },
    { path: '/work-orders', label: 'Work Orders', icon: ClipboardList },
    { path: '/inspections', label: 'Inspections', icon: FileCheck },
    { path: '/checklists', label: 'Checklists', icon: CheckSquare },
    { path: '/calendar', label: 'Calendar', icon: CalendarDays },
    { path: '/maintenance', label: 'Maintenance', icon: Wrench },
    { path: '/vendors', label: 'Vendors', icon: Truck },
    { path: '/budget', label: 'Budget', icon: Wallet },
    { path: '/sustainability', label: 'Sustainability', icon: Leaf },
    { path: '/floor-plans', label: 'Space Visualisation', icon: Map },
    { path: '/ppm', label: 'Preventive Maintenance', icon: Repeat },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/report-builder', label: 'Report Builder', icon: FileText },
    { path: '/analytics', label: 'Analytics', icon: LineChart },
  ];

  if (canManageUsers) {
    navItems.push({ path: '/users', label: 'Users', icon: Users });
  }

  if (isAdmin) {
    navItems.push({ path: '/admin/settings', label: 'Admin Settings', icon: Shield });
    navItems.push({ path: '/admin/audit-logs', label: 'Audit Trail', icon: ScrollText });
    navItems.push({ path: '/admin/saved-view-templates', label: 'View Templates', icon: Bookmark });
    navItems.push({ path: '/admin/leads', label: 'Demo Requests', icon: MessageSquare });
    navItems.push({ path: '/operations', label: 'Operations', icon: ShieldCheck });
  }

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path || 
                     (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
    const Icon = item.icon;

    return (
      <NavLink
        to={item.path}
        onClick={() => setIsOpen(false)}
        className={cn(
          'sidebar-item',
          isActive && 'sidebar-item-active'
        )}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#111565] text-white rounded-md shadow-lg touch-target"
        data-testid="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-full w-64 bg-[#111565] text-white z-40 flex flex-col noise-overlay',
          'transform transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">FacilityPro</h1>
              <p className="text-xs text-white/60 mt-1 uppercase tracking-wider">
                Facility Management
              </p>
            </div>
          </div>
        </div>

        {/* Offline Status */}
        <div className="px-4 py-2 border-b border-white/10">
          <OfflineStatusIndicator />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#ff6600] flex items-center justify-center font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>

          <NavLink
            to="/settings"
            onClick={() => setIsOpen(false)}
            className={cn(
              'sidebar-item',
              location.pathname === '/settings' && 'sidebar-item-active'
            )}
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" strokeWidth={1.5} />
            <span>Settings</span>
          </NavLink>

          <button
            onClick={logout}
            className="sidebar-item w-full text-left text-red-300 hover:text-red-200 hover:bg-red-500/20"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.5} />
            <span>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
